import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Header, Footer } from "@/components/Layout";
import { toast } from "sonner";
import api from "@/lib/api";
import NotificationBell from "@/components/dashboard/NotificationBell";
import OrdersCard from "@/components/dashboard/OrdersCard";
import ProfileCard from "@/components/dashboard/ProfileCard";
import QuickActions from "@/components/dashboard/QuickActions";
import SubscriptionCard from "@/components/dashboard/SubscriptionCard";
import { getDashboardCopy } from "@/components/dashboard/dashboardCopy";
import { sortOrdersForDashboard, userCreditCount } from "@/components/dashboard/dashboardHelpers";

const DashboardPage = () => {
  const { user, updateProfile } = useAuth();
  const { language } = useLanguage();
  const copy = getDashboardCopy(language);
  const [orders, setOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    whatsapp: user?.whatsapp || "",
    club: user?.club || "",
  });

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } catch {
      toast.error(copy.page.loadOrdersError);
    } finally {
      setLoading(false);
    }
  }, [copy.page.loadOrdersError]);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data);
    } catch {
      // Les notifications ne doivent jamais bloquer le dashboard.
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchNotifications();
  }, [fetchOrders, fetchNotifications]);

  const markNotificationRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`, {});
      setNotifications((current) => current.map((item) => item.notification_id === notificationId ? { ...item, read: true } : item));
    } catch {
      toast.error(copy.page.notificationReadError);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.post("/notifications/read-all", {});
      setNotifications((current) => current.map((item) => ({ ...item, read: true })));
    } catch {
      toast.error(copy.page.notificationReadAllError);
    }
  };

  const handleCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const response = await api.post("/payments/customer-portal", { return_url: window.location.href });
      if (!response.data?.url) throw new Error("missing_portal_url");
      window.location.href = response.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || copy.page.portalError);
      setPortalLoading(false);
    }
  };

  const handleProfileUpdate = async (event) => {
    event.preventDefault();
    try {
      await updateProfile(profileData);
      setEditingProfile(false);
      toast.success(copy.page.profileUpdated);
    } catch {
      toast.error(copy.page.profileUpdateError);
    }
  };

  const sortedOrders = sortOrdersForDashboard(orders);
  const totalCredits = userCreditCount(user);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-black uppercase text-white mb-2">{copy.page.title}</h1>
              <p className="text-zinc-500">{copy.page.welcome}, {user?.first_name}</p>
            </div>
            <NotificationBell notifications={notifications} onRead={markNotificationRead} onReadAll={markAllNotificationsRead} copy={copy.notifications} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <SubscriptionCard user={user} totalCredits={totalCredits} onCustomerPortal={handleCustomerPortal} portalLoading={portalLoading} copy={copy.subscription} />
              <OrdersCard loading={loading} orders={sortedOrders} copy={copy} language={language} />
            </div>

            <div className="space-y-6">
              <ProfileCard
                user={user}
                editingProfile={editingProfile}
                setEditingProfile={setEditingProfile}
                profileData={profileData}
                setProfileData={setProfileData}
                onSubmit={handleProfileUpdate}
                copy={copy.profile}
              />
              <QuickActions hasCredits={totalCredits > 0} copy={copy.quick} />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DashboardPage;
